import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, Save } from "lucide-react";

function slugify(s) {
  return (s || "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 120);
}

export default function SuperAdminKbEdit() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug;
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [art, setArt] = useState({
    title: "", slug: "", summary: "", body_markdown: "",
    category: "", tags: [], status: "draft",
  });
  const tagInput = useMemo(() => (art.tags || []).join(", "), [art.tags]);

  useEffect(() => {
    api.get("/kb/categories").then(({ data }) => setCategories(data.items || []));
    if (!isNew) {
      api.get(`/kb/articles/${slug}`)
        .then(({ data }) => setArt({
          title: data.title || "", slug: data.slug || "",
          summary: data.summary || "", body_markdown: data.body_markdown || "",
          category: data.category || "", tags: data.tags || [], status: data.status || "draft",
        }))
        .catch((e) => toast.error(formatApiError(e)))
        .finally(() => setLoading(false));
    }
  }, [slug, isNew]);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: art.title, slug: art.slug || slugify(art.title),
        summary: art.summary, body_markdown: art.body_markdown,
        category: art.category, tags: art.tags, status: art.status,
      };
      if (isNew) {
        const { data } = await api.post("/super-admin/kb/articles", payload);
        toast.success("Article created");
        navigate(`/super-admin/kb/${data.slug}/edit`);
      } else {
        await api.patch(`/super-admin/kb/articles/${slug}`, payload);
        toast.success("Article saved");
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <AppShell title="Article"><div className="max-w-5xl mx-auto py-8 px-4 text-xs text-navy-400">Loading…</div></AppShell>;

  return (
    <AppShell title={isNew ? "New article" : art.title || "Edit article"}>
      <div className="max-w-7xl mx-auto py-8 px-4">
        <Link to="/super-admin/kb" className="inline-flex items-center text-sm text-navy-500 hover:text-navy-900 mb-3" data-testid="super-kb-edit-back">
          <ChevronLeft className="w-4 h-4 mr-1" /> Knowledge base
        </Link>
        <h1 className="text-2xl font-semibold mb-4" data-testid="super-kb-edit-heading">{isNew ? "New article" : "Edit article"}</h1>

        <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="eb-card p-5 space-y-3">
            <Field label="Title">
              <input
                className="w-full border border-navy-200 rounded-md px-3 py-2 text-sm"
                value={art.title}
                onChange={(e) => setArt({ ...art, title: e.target.value, slug: isNew && !art.slug ? slugify(e.target.value) : art.slug })}
                data-testid="super-kb-edit-title" required
              />
            </Field>
            <Field label="Slug">
              <input
                className="w-full border border-navy-200 rounded-md px-3 py-2 text-sm font-mono"
                value={art.slug}
                onChange={(e) => setArt({ ...art, slug: slugify(e.target.value) })}
                data-testid="super-kb-edit-slug" required
              />
            </Field>
            <Field label="Category">
              <Select value={art.category} onValueChange={(v) => setArt({ ...art, category: v })}>
                <SelectTrigger data-testid="super-kb-edit-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tags (comma-separated)">
              <input
                className="w-full border border-navy-200 rounded-md px-3 py-2 text-sm"
                value={tagInput}
                onChange={(e) => setArt({ ...art, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                data-testid="super-kb-edit-tags"
              />
            </Field>
            <Field label="Summary (≤400 chars)">
              <textarea
                className="w-full border border-navy-200 rounded-md px-3 py-2 text-sm"
                rows={3}
                value={art.summary}
                maxLength={400}
                onChange={(e) => setArt({ ...art, summary: e.target.value })}
                data-testid="super-kb-edit-summary" required
              />
            </Field>
            <Field label="Status">
              <Select value={art.status} onValueChange={(v) => setArt({ ...art, status: v })}>
                <SelectTrigger data-testid="super-kb-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Body (Markdown)">
              <textarea
                className="w-full border border-navy-200 rounded-md px-3 py-2 text-sm font-mono"
                rows={16}
                value={art.body_markdown}
                onChange={(e) => setArt({ ...art, body_markdown: e.target.value })}
                data-testid="super-kb-edit-body" required
              />
            </Field>
            <div className="flex justify-end">
              <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="super-kb-edit-save">
                <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>

          <div className="eb-card p-5">
            <div className="text-[10px] uppercase tracking-wider text-navy-500 font-semibold mb-3">Live preview</div>
            <h2 className="text-xl font-semibold mb-2">{art.title || "Untitled article"}</h2>
            <Markdown text={art.body_markdown || "_Start typing to preview…_"} />
          </div>
        </form>
      </div>
    </AppShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider text-navy-500 font-semibold mb-1">{label}</label>
      {children}
    </div>
  );
}
